import type {
  FiscalWorkstationStorage,
  KkmServerMappedReceiptPlan,
  MapMorsReceiptPlanInput,
} from '@/entities/fiscalization';
import type { KkmExecutionResult, KkmHandshakeResult, KkmServerAdapter } from '@/shared/api';

import {
  type OrderFieldsFragment,
  type OrderFiscalExecutionFieldsFragment,
  type OrderFiscalOperationIdentityInput,
  OrderFiscalOperationStatus,
  OrderFiscalRecoveryAction,
  type OrderFiscalRefundExecutionFieldsFragment,
  type OrderFiscalRefundResultOutcome,
  type OrderFiscalSettlementResultInput,
  type OrderFiscalSettlementResultOutcome,
} from '@shared/api/graphql/gql/graphql';

import type { OrderFiscalizationPhase, OrderFiscalizationState } from './order-fiscalization-state';
import type { OrderFiscalRecoverOptions } from './order-fiscalization-types';

import { executeKkmSafely } from './execute-kkm-safely';
import { kktShiftClosedAfterFailure, LiveKktIdentity, readExecutionReceiptDocuments } from './fiscal-operation-session';
import {
  acquiringCommandId,
  commandStatus,
  fiscalResultErrorFields,
  isFiscalResultValidationError,
  isReceiptRetryAllowed,
  mapFiscalExecutionReceipt,
  safeErrorMessage,
} from './order-fiscal-result';
import { createInitialOrderFiscalizationState } from './order-fiscalization-state';

type ReceiptMessages = {
  captured: string;
  unknown: string;
  question: string;
  fallback?: string;
  finalizing: string;
  pendingSync: string;
  invalidResult: string;
  retry: string;
  failedPrevious: string;
};

type CompletionResult<TExecution> = { execution: TExecution; order: OrderFieldsFragment };

type UnfiscalizedInput<TOutcome> = OrderFiscalOperationIdentityInput & {
  result: Pick<OrderFiscalSettlementResultInput, 'commandStatus' | 'errorCode' | 'errorMessage' | 'kkm'> & {
    outcome: TOutcome;
  };
};

type CompletionDependencies<TExecution, TInput> = {
  services: {
    complete: (input: TInput) => Promise<CompletionResult<TExecution>>;
    find: (orderId: string) => Promise<TExecution | null>;
  };
  storage: Pick<FiscalWorkstationStorage, 'clearPendingOperation'>;
};

/** Общий lifecycle одной фискальной операции; денежные решения остаются в конкретном контроллере. */
export abstract class FiscalOperationController<
  TExecution extends OrderFiscalExecutionFieldsFragment | OrderFiscalRefundExecutionFieldsFragment,
  TFinalize,
> {
  protected busy = false;
  protected readonly liveKkt = new LiveKktIdentity();
  protected pendingFinalize: TFinalize | null = null;
  protected finalizeExecution: TExecution | null = null;
  /** Разрешённый сервером повтор печати. */
  protected retryableExecution: TExecution | null = null;
  /** Операция, чей чек ещё предстоит подтвердить. */
  protected resolvableExecution: TExecution | null = null;
  protected receiptQuestionOpen = false;
  protected stateValue: OrderFiscalizationState = createInitialOrderFiscalizationState();

  protected constructor(
    private readonly adapter: Pick<KkmServerAdapter, 'getDataCheck' | 'execute' | 'getResult' | 'handshake'>,
    private readonly notify: (state: Readonly<OrderFiscalizationState>) => void,
    private readonly messages: ReceiptMessages,
    private readonly completion: CompletionDependencies<TExecution, TFinalize>,
  ) {}

  public get state(): Readonly<OrderFiscalizationState> {
    return this.stateValue;
  }

  public reset(): boolean {
    if (this.busy || !this.stateValue.canReset) return false;
    this.pendingFinalize = null;
    this.retryableExecution = null;
    this.resolvableExecution = null;
    this.receiptQuestionOpen = false;
    this.resetContext();
    this.liveKkt.clear();
    this.stateValue = createInitialOrderFiscalizationState();
    this.emitState();
    return true;
  }

  public async recover(orderId: string, options: OrderFiscalRecoverOptions = {}): Promise<OrderFieldsFragment | null> {
    if (this.busy) return null;
    this.busy = true;
    this.retryableExecution = null;
    const cycle = options.startPayment === false;

    try {
      const execution = await this.completion.services.find(orderId);
      return await this.recoverOrder(execution, orderId, cycle);
    } catch (error) {
      this.transition('print_result_unknown', this.errorMessage(error));
      return null;
    } finally {
      this.busy = false;
    }
  }

  public async retryFinalize(): Promise<OrderFieldsFragment | null> {
    if (this.busy || this.pendingFinalize === null) return null;
    this.busy = true;
    this.transition('finalizing', this.messages.finalizing);

    try {
      const completed = await this.completion.services.complete(this.pendingFinalize);
      return this.acceptCompletion(completed, completed.execution.operation.id);
    } catch (error) {
      if (isFiscalResultValidationError(error)) {
        this.pendingFinalize = null;
        const execution = this.finalizeExecution;
        if (execution === null) this.transition('print_result_unknown', this.messages.invalidResult);
        else this.showResolveReceipt(execution);
      } else {
        this.transition('printed_pending_sync', this.messages.pendingSync);
      }
      return null;
    } finally {
      this.busy = false;
    }
  }

  public async retryReceipt(): Promise<OrderFieldsFragment | null> {
    const execution = this.retryableExecution;
    if (this.busy || execution === null) return null;
    this.busy = true;
    this.retryableExecution = null;

    try {
      return await this.retryAndPrint(execution);
    } catch (error) {
      this.retryableExecution = execution;
      this.transition('payment_without_receipt', this.errorMessage(error), execution);
      return null;
    } finally {
      this.busy = false;
    }
  }

  public async resolveReceipt(): Promise<OrderFieldsFragment | null> {
    const execution = this.resolvableExecution;
    if (this.busy || execution === null) return null;
    this.busy = true;

    try {
      return await this.resolveReceiptFor(execution, true);
    } catch (error) {
      this.showResolveReceipt(execution, this.errorMessage(error));
      return null;
    } finally {
      this.busy = false;
    }
  }

  public async answerReceiptPrinted(printed: boolean): Promise<OrderFieldsFragment | null> {
    const execution = this.resolvableExecution;
    if (this.busy || execution === null || !this.receiptQuestionOpen) return null;
    this.busy = true;

    try {
      this.receiptQuestionOpen = false;
      return await this.answerReceiptFor(execution, printed);
    } catch (error) {
      this.showReceiptQuestion(execution, this.errorMessage(error));
      return null;
    } finally {
      this.busy = false;
    }
  }

  /** undefined — шаг остаётся конкретному контроллеру. */
  protected recoverReceiptStep(
    execution: TExecution,
    cycle: boolean,
  ): Promise<OrderFieldsFragment | null> | null | undefined {
    if (execution.operation.status === OrderFiscalOperationStatus.Completed) {
      if (cycle && execution.operation.receipt_confirmed_at != null) return this.resolveReceiptFor(execution, false);
      this.completion.storage.clearPendingOperation(execution.operation.id);
      this.transition('completed', null, execution);
      return null;
    }

    if (execution.operation.status === OrderFiscalOperationStatus.Failed) {
      this.completion.storage.clearPendingOperation(execution.operation.id);
      this.transition('failed_before_print', this.messages.failedPrevious, execution);
      return null;
    }

    if (execution.next_action === OrderFiscalRecoveryAction.ResolveReceipt) {
      if (cycle) return this.resolveReceiptFor(execution, false);
      this.showResolveReceipt(execution);
      return null;
    }

    if (
      execution.next_action === OrderFiscalRecoveryAction.PrintReceipt &&
      execution.operation.status === OrderFiscalOperationStatus.PaymentCapturedUnfiscalized
    ) {
      if (cycle) return this.retryAndPrint(execution);
      this.showReceiptRetry(execution);
      return null;
    }

    return undefined;
  }

  protected async pollPrepared(
    execution: TExecution,
  ): Promise<{ hasCard: boolean; result: KkmExecutionResult } | null> {
    const mapped = await this.mapPrepared(execution);
    if (mapped === null) return null;

    const acquiringDeviceKey = mapped.acquiringCommand !== null ? mapped.acquiringDeviceKey : null;
    const hasCard = acquiringDeviceKey !== null;
    const result = await executeKkmSafely(() =>
      this.adapter.getResult(hasCard ? acquiringCommandId(execution.id_command) : execution.id_command, {
        deviceKey: acquiringDeviceKey ?? mapped.registerDeviceKey,
        timeoutClass: hasCard ? 'acquiring' : 'kkt',
      }),
    );
    return { hasCard, result };
  }

  protected showReceiptRetry(execution: TExecution): boolean {
    if (!isReceiptRetryAllowed(execution)) return false;
    this.retryableExecution = execution;
    this.transition('payment_without_receipt', this.messages.retry, execution);
    return true;
  }

  protected async savePrintedReceipt(execution: TExecution, input: TFinalize): Promise<OrderFieldsFragment | null> {
    this.pendingFinalize = input;
    this.finalizeExecution = execution;
    this.transition('finalizing', this.messages.finalizing, execution);

    try {
      const completed = await this.completion.services.complete(input);
      return this.acceptCompletion(completed, execution.operation.id);
    } catch {
      this.transition('printed_pending_sync', this.messages.pendingSync, execution);
      return null;
    }
  }

  private acceptCompletion(
    completed: CompletionResult<TExecution>,
    pendingOperationId: string,
  ): OrderFieldsFragment | null {
    this.pendingFinalize = null;
    if (completed.execution.operation.status !== OrderFiscalOperationStatus.Completed) {
      this.showResolveReceipt(completed.execution);
      return null;
    }
    this.completion.storage.clearPendingOperation(pendingOperationId);
    this.transition('completed', null, completed.execution);
    return completed.order;
  }

  protected errorMessage(error: unknown): string {
    return safeErrorMessage(error, this.messages.fallback);
  }

  protected showResolveReceipt(execution: TExecution, message: string | null = null): void {
    this.resolvableExecution = execution;
    this.receiptQuestionOpen = false;
    const captured = execution.operation.status === OrderFiscalOperationStatus.PaymentCapturedUnfiscalized;
    this.transition(
      captured ? 'payment_without_receipt' : 'print_result_unknown',
      message ?? (captured ? this.messages.captured : this.messages.unknown),
      execution,
    );
  }

  protected showReceiptQuestion(execution: TExecution, message: string | null = null): void {
    this.resolvableExecution = execution;
    this.receiptQuestionOpen = true;
    this.transition('print_result_unknown', message ?? this.messages.question, execution);
  }

  protected async executeReceipt(
    execution: TExecution,
    message: string,
    mapper?: (input: MapMorsReceiptPlanInput) => KkmServerMappedReceiptPlan,
  ): Promise<KkmExecutionResult> {
    const mapped = mapFiscalExecutionReceipt(execution, mapper);
    this.transition('printing', message, execution);
    return executeKkmSafely(() =>
      this.adapter.execute(mapped.registerCheckCommand, {
        deviceKey: mapped.registerDeviceKey,
        timeoutClass: 'kkt',
      }),
    );
  }

  protected async completePrintFailure<
    TOutcome extends OrderFiscalRefundResultOutcome | OrderFiscalSettlementResultOutcome,
  >(
    execution: TExecution,
    result: KkmExecutionResult,
    outcome: TOutcome,
    complete: (input: UnfiscalizedInput<TOutcome>) => Promise<CompletionResult<TExecution>>,
  ) {
    const kktShiftClosedExternally =
      result.kind !== 'ok' &&
      (await kktShiftClosedAfterFailure(
        this.adapter,
        execution.operation.id,
        execution.execution_target.kkt_device_id,
      ));
    let unreceipted: TExecution | null = null;
    if (result.kind !== 'ok' && execution.operation.status === OrderFiscalOperationStatus.PaymentCaptured) {
      const completed = await complete({
        ...this.liveKkt.identityFor(execution),
        result: {
          commandStatus: commandStatus(result),
          ...fiscalResultErrorFields(result, execution.operation.id),
          // Сырой ответ ККТ: по нему сервер доказывает, что документ не создан.
          kkm: this.liveKkt.kkmResponse(result.response.raw),
          outcome,
        },
      });
      unreceipted = completed.execution;
    }

    return { kktShiftClosedExternally, unreceipted };
  }

  protected resetContext(): void {
    // Дополнительные данные оплаты очищает её контроллер до публикации нового состояния.
  }

  protected emitState(): void {
    this.notify(this.stateValue);
  }

  protected readReceiptDocuments(execution: TExecution): ReturnType<typeof readExecutionReceiptDocuments> {
    this.resolvableExecution = execution;
    this.receiptQuestionOpen = false;
    this.transition('checking_receipt', 'Проверяем чек в кассе.', execution);
    return readExecutionReceiptDocuments(this.adapter, execution, this.liveKkt, () =>
      this.handshakeBoundKkt(execution.execution_target.kkt_device_id),
    );
  }

  protected abstract mapPrepared(execution: TExecution): Promise<KkmServerMappedReceiptPlan | null>;
  protected abstract recoverOrder(
    execution: TExecution | null,
    orderId: string,
    cycle: boolean,
  ): Promise<OrderFieldsFragment | null>;
  protected abstract retryAndPrint(execution: TExecution): Promise<OrderFieldsFragment | null>;
  protected abstract resolveReceiptFor(execution: TExecution, ask: boolean): Promise<OrderFieldsFragment | null>;
  protected abstract answerReceiptFor(execution: TExecution, printed: boolean): Promise<OrderFieldsFragment | null>;
  protected abstract handshakeBoundKkt(kktDeviceId: string | null | undefined): Promise<KkmHandshakeResult>;
  protected abstract transition(phase: OrderFiscalizationPhase, message?: string | null, execution?: TExecution): void;
}
